import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as authService from '@/app/services/authService';
import { getCurrentUserInfo } from '@/app/services/userService';
import { LoginCredentials, UserProfile } from '@/app/types/auth';
import axios, { AxiosError } from 'axios';

interface AuthState {
    token: string | null;
    user: UserProfile | null;
    userOrgId: number | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    loginAction: (credentials: LoginCredentials) => Promise<void>;
    logoutAction: () => void;
    setToken: (token: string | null) => void;
    fetchUserProfileAction: () => Promise<void>;
}

// Use localStorage for storage
const storage = createJSONStorage(() => localStorage);

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            token: null,
            user: null,
            userOrgId: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            setToken: (token: string | null): void => {
                set({ token, isAuthenticated: !!token });
            },

            loginAction: async (credentials: LoginCredentials): Promise<void> => {
                set({ isLoading: true, error: null });
                try {
                    const response = await authService.login(credentials);
                    const token = response.access_token;
                    
                    // Set token and authenticated state
                    set({
                        token: token,
                        isAuthenticated: true,
                        // Keep isLoading true until user profile is fetched
                    });
                    
                    // Store token in localStorage manually as well
                    localStorage.setItem('authToken', token);
                    
                    // Fetch user profile after token is set
                    await get().fetchUserProfileAction();
                    
                    // Set loading to false after user profile is fetched
                    set({ isLoading: false });
                } catch (error: unknown) {
                    let errorMessage = 'Login failed';
                    if (axios.isAxiosError(error)) {
                        const axiosError = error as AxiosError<{ detail?: string }>;
                        errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Login failed';
                    } else if (error instanceof Error) {
                        errorMessage = error.message;
                    }
                    
                    set({
                        error: errorMessage,
                        isLoading: false,
                        isAuthenticated: false,
                        token: null,
                        user: null,
                        userOrgId: null
                    });
                    // Clear any potentially persisted token on failure
                    localStorage.removeItem('authToken');
                }
            },

            fetchUserProfileAction: async (): Promise<void> => {
                try {
                    const userProfile = await getCurrentUserInfo();
                    const orgId = userProfile?.organization_id || userProfile?.organization?.id || null;
                    
                    set({ 
                        user: userProfile, 
                        userOrgId: orgId,
                        error: null 
                    });
                    
                    // If we have organization ID and token, we're ready for DB operations
                    if (orgId && get().token) {
                        // Here we could set organizationId in main process if needed
                        // For now we're just using the auth token
                    }
                } catch (error: unknown) {
                    let errorMessage = 'Failed to fetch user profile';
                    if (axios.isAxiosError(error)) {
                        const axiosError = error as AxiosError<{ detail?: string }>;
                        errorMessage = axiosError.response?.data?.detail || axiosError.message || 'Failed to fetch profile';
                    } else if (error instanceof Error) {
                        errorMessage = error.message;
                    }
                    console.error("Error fetching user profile:", errorMessage);
                    
                    // Set error but don't clear user or token
                    set({ error: errorMessage });
                }
            },

            logoutAction: (): void => {
                set({
                    token: null,
                    user: null,
                    userOrgId: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: null,
                });
                // Clear persisted token
                localStorage.removeItem('authToken');
                
                // Perform full page refresh to sync token states
                window.location.href = '/login';
            },
        }),
        {
            name: 'auth-storage', // Name for the persisted data key
            storage: storage,
            partialize: (state) => ({ 
                token: state.token, 
                isAuthenticated: state.isAuthenticated,
                user: state.user, // Also persist the user data
                userOrgId: state.userOrgId // Persist the organization ID
            }),
        }
    )
); 